import React, { useState } from 'react';
import Chip from '../shared/Chip';
import Icon from '../shared/Icon';
import ListTable from '../shared/ListTable';
import Timestamp from '../shared/Timestamp';
import {
  ACTIVITY_CATEGORY_CHIP,
  activityCategory,
  activityStatus,
  activityUsage,
} from '../../../models/ActivitiesModel';
import '../Library/Library.css'; // library-cell, library-icon-trigger, library-sr-only

const cut = (text) => <span className="library-cell list-table-cut">{text || '-'}</span>;

const detailId = (entry) => `activity-detail-${entry.id}`;

// The record under a row shows only what the row doesn't: what it was done
// to, the context and who did it, each only when there is one. A Field List,
// label over value, without its rules (ResUI Design Decisions, Tables).
function ActivityRecord({ entry }) {
  const fields = [['Details', entry.target], ['Context', entry.details], ['By', entry.actor]].filter(([, v]) => v);
  return (
    <dl className="activities-record">
      {fields.map(([label, value]) => (
        <div key={label} className="activities-record-field">
          <dt className="activities-record-label">{label}</dt>
          <dd className="activities-record-value">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The Activity log on the shared List table: Time (pinned, static form),
 * Category, Action, Usage, Status and the caret that opens the record: the
 * Icon trigger with a 20 caret in its own 44 cell, turning up while the record
 * is open (ResUI Design Decisions, Tables). A click anywhere on the row opens
 * it too; the caret is the keyboard control, so the row keeps its table roles. Nothing here is editable. A new sort,
 * search or filter closes what was open, as the prototype's redraw does.
 */
function ActivityTable({ entries, sort, onSortChange, resetKey }) {
  const signature = `${sort.key}|${sort.dir}|${resetKey}`;
  const [opened, setOpened] = useState({ signature, ids: [] });
  const open = opened.signature === signature ? opened.ids : [];
  const isOpen = (entry) => open.includes(entry.id);
  const toggle = (entry) => setOpened({
    signature,
    ids: isOpen(entry) ? open.filter((id) => id !== entry.id) : [...open, entry.id],
  });

  const columns = [
    // The handoff's 200 fits its own "3 November 2026, 16:42". The static form
    // adds the zone and may run on the 12-hour clock, so 264 keeps a named
    // zone whole ("28 September 2026, 12:52 PM ADT"); an offset zone cuts,
    // with the time in full in the tooltip.
    {
      key: 'time',
      title: 'Time',
      width: '264px',
      sort: 'date',
      value: (e) => Date.parse(e.time),
      render: (e) => <span className="list-table-cut"><Timestamp value={e.time} form="static" /></span>,
    },
    {
      key: 'category',
      title: 'Category',
      width: '136px',
      sort: 'text',
      value: activityCategory,
      render: (e) => <Chip variant={ACTIVITY_CATEGORY_CHIP[activityCategory(e)]}>{activityCategory(e)}</Chip>,
    },
    { key: 'action', title: 'Action', width: 'minmax(200px, 1fr)', sort: 'text', value: (e) => e.action, render: (e) => cut(e.action) },
    { key: 'usage', title: 'Usage', width: '132px', sort: 'text', value: activityUsage, render: (e) => cut(activityUsage(e)) },
    { key: 'status', title: 'Status', width: '112px', sort: 'text', value: activityStatus, render: (e) => cut(activityStatus(e)) },
    {
      key: 'details',
      title: <span className="library-sr-only">Details</span>,
      width: '44px',
      cellClassName: 'library-cell--menu',
      render: (e) => (
        <button
          type="button"
          className="library-icon-trigger activities-caret"
          aria-expanded={isOpen(e)}
          aria-controls={detailId(e)}
          aria-label={`Details for ${e.action}`}
          onClick={() => toggle(e)}
        >
          <Icon name="caret-down" size={20} />
        </button>
      ),
    },
  ];

  return (
    <ListTable
      label="Activity log"
      columns={columns}
      rows={entries}
      rowKey={(e) => e.id}
      rowProps={(e) => ({
        className: `activities-row${isOpen(e) ? ' is-open' : ''}`,
        // The caret handles its own click.
        onClick: (ev) => { if (!ev.target.closest('button')) toggle(e); },
      })}
      detail={(e) => ({ id: detailId(e), open: isOpen(e), content: <ActivityRecord entry={e} /> })}
      sort={sort}
      onSortChange={onSortChange}
      resetKey={resetKey}
    />
  );
}

export default ActivityTable;
